import { useEffect, useMemo, useState } from 'react';

import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import {
    arrayBufferToString,
    generateTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { maxPageSize, smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';

export const useStatusChangesLogic = ({ ui, api, table, title, subTitle, isSinglePage }) => {
    const [spinnerOpen, setSpinnerOpen] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(smallPageSize);
    const finalPageSize = useMemo(() => (isSinglePage ? maxPageSize : pageSize), [pageSize, isSinglePage]);

    const reportType = api.getStatusChangesTableData.instance.data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId: ui.state.projectId,
        contractId: ui.state.contractId,
        latestVersionId: ui.state.latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleAPIExcelExport = () => {
        setSpinnerOpen(true);
        api.exportCsvReport.instance.mutate(api.exportCsvReport.createPayload({ reportType, fileName }), {
            onSuccess: () => {
                setSpinnerOpen(false);
                handleSnackBar('File Successfully Downloaded', 'success');
                sendEventWithDimensions({
                    category: 'Export',
                    action: 'Excel',
                    label: `${title} ${subTitle}`,
                });
            },
            onError: (error) => {
                setSpinnerOpen(false);
                const message = arrayBufferToString(error?.response?.data);
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
        });
    };

    const tabsArrData = generateTabs({ data: api.getStatusChangesTableData.instance.data });

    const currentTabItems = useMemo(() => {
        if (
            api.getStatusChangesTableData.instance.data &&
            api.getStatusChangesTableData.instance.data.periodItemsList
        ) {
            const periodItemsList = api.getStatusChangesTableData.instance.data.periodItemsList;
            return (
                periodItemsList.find((item) => item.id === ui.state.activeTab)?.itemsList ||
                periodItemsList[0].itemsList
            );
        }
        return [];
    }, [api.getStatusChangesTableData.instance.data, ui.state.activeTab]);

    const filteredData = useMemo(() => {
        const isCriticalPath = ui.state.isCriticalPath;
        const selectedStatuses = ui.state.status?.map((item) => item.value);

        if (currentTabItems) {
            return currentTabItems
                .filter((item) => selectedStatuses?.includes(item.latestVersion.cardBoardViewResponse.status.name))
                .filter((item) =>
                    isCriticalPath
                        ? item.latestVersion.cardBoardViewResponse.cardIndicators.isCriticalPath.isIndicatorOn ===
                          isCriticalPath
                        : item,
                );
        }
        return null;
    }, [currentTabItems, ui.state.status, ui.state.isCriticalPath, table.sorting]);

    useEffect(() => {
        table.setPagination((prev) => ({ ...prev, size: finalPageSize }));
    }, [finalPageSize]);

    return {
        logic: {
            tabsArrData,
            filteredData,
            spinnerOpen,
            handleAPIExcelExport,
            pageSize,
            setPageSize,
            finalPageSize,
        },
    };
};
